import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import Iconify from "../../../components/iconify";
import { getStatusMeta, toCurrency } from "./shopUtils";
import {
  buildCompactOrderSummary,
  formatOrderDate,
  INVOICE_DOWNLOADABLE_ORDER_STATUSES,
} from "./shopCatalogHelpers";

const FULFILLMENT_STEPS = [
  { key: "new", label: "Bestellt" },
  { key: "picking", label: "Wird vorbereitet" },
  { key: "shipped", label: "Versandt" },
  { key: "delivered", label: "Zugestellt" },
];

const FULFILLMENT_STEP_INDEX = {
  new: 0,
  picking: 1,
  packed: 1,
  shipped: 2,
  delivered: 3,
};

function MemberFulfillmentProgress({ fulfillmentStatus, trackingNumber }) {
  const currentIndex = FULFILLMENT_STEP_INDEX[fulfillmentStatus] ?? 0;
  const isIssue = fulfillmentStatus === "issue";

  if (isIssue) {
    return (
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Iconify
          icon="mdi:alert-circle-outline"
          width={16}
          color="error.main"
        />
        <Typography variant="caption" color="error.main" fontWeight={600}>
          Es gibt ein Problem mit dem Versand. Bitte kontaktiere uns.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={0} alignItems="center">
        {FULFILLMENT_STEPS.map((step, index) => {
          const isCompleted = index <= currentIndex;
          const isLast = index === FULFILLMENT_STEPS.length - 1;

          return (
            <Stack
              key={step.key}
              direction="row"
              alignItems="center"
              sx={{ flex: isLast ? "none" : 1 }}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: isCompleted ? "primary.main" : "grey.300",
                  flexShrink: 0,
                }}
              >
                {isCompleted ? (
                  <Iconify
                    icon="eva:checkmark-fill"
                    width={14}
                    sx={{ color: "white" }}
                  />
                ) : (
                  <Typography
                    variant="caption"
                    sx={{ color: "white", fontSize: 10, fontWeight: 700 }}
                  >
                    {index + 1}
                  </Typography>
                )}
              </Box>
              {!isLast ? (
                <Box
                  sx={{
                    flex: 1,
                    height: 2,
                    bgcolor: index < currentIndex ? "primary.main" : "grey.300",
                    mx: 0.25,
                  }}
                />
              ) : null}
            </Stack>
          );
        })}
      </Stack>

      <Stack direction="row" justifyContent="space-between">
        {FULFILLMENT_STEPS.map((step, index) => (
          <Typography
            key={step.key}
            variant="caption"
            color={index <= currentIndex ? "text.primary" : "text.disabled"}
            fontWeight={index === currentIndex ? 700 : 400}
            sx={{ fontSize: 10, textAlign: "center", flex: 1 }}
          >
            {step.label}
          </Typography>
        ))}
      </Stack>

      {trackingNumber ? (
        <Typography variant="caption" color="text.secondary">
          Sendungsnr.: {trackingNumber}
        </Typography>
      ) : null}
    </Stack>
  );
}

const MemberOrderCardSkeleton = () => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.25 }}>
    <Stack spacing={1.2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box sx={{ width: "100%", maxWidth: 360 }}>
          <Skeleton width="72%" height={26} />
          <Skeleton width="48%" height={18} />
        </Box>
        <Skeleton width={128} height={28} />
      </Stack>
      <Skeleton variant="rounded" height={54} />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Skeleton width={72} height={18} />
        <Skeleton width={110} height={24} />
      </Stack>
      <Skeleton variant="rounded" height={38} />
    </Stack>
  </Paper>
);

export default function ShopCatalogOrdersView({
  myOrders,
  loadingMyOrders,
  activeOrderActionId,
  activeInvoiceOrderId,
  expandedOrderId,
  onToggleExpanded,
  onOpenCancelConfirm,
  onOpenCorrectConfirm,
  onDownloadInvoice,
}) {
  return (
    <Stack spacing={1.5}>
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        Solange der Status auf <strong>Neu</strong> steht, kannst du die
        Bestellung stornieren oder zur Korrektur wieder in den Warenkorb
        übernehmen.
      </Alert>

      {loadingMyOrders ? (
        <Stack spacing={1.25}>
          {Array.from({ length: 3 }).map((_, index) => (
            <MemberOrderCardSkeleton key={`my-order-skeleton-${index}`} />
          ))}
        </Stack>
      ) : myOrders.length ? (
        <Stack spacing={1.25}>
          {myOrders.map((order) => {
            const statusMeta = getStatusMeta(order.status);
            const isEditable = order.status === "created";
            const canDownloadInvoice = INVOICE_DOWNLOADABLE_ORDER_STATUSES.has(
              String(order.status || ""),
            );
            const isBusy = activeOrderActionId === order._id;
            const isExpanded = expandedOrderId === order._id;

            return (
              <Paper
                key={order._id}
                variant="outlined"
                sx={{
                  borderRadius: 2.25,
                  p: 2,
                  borderColor: isEditable ? "primary.light" : "divider",
                }}
              >
                <Stack spacing={1.25}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        Bestellung vom {formatOrderDate(order.createdAt)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {order._id}
                      </Typography>
                    </Box>

                    <Chip
                      size="small"
                      color={statusMeta.color}
                      label={statusMeta.label}
                      sx={{ fontWeight: 700 }}
                    />
                  </Stack>

                  {order.fulfillment?.type === "local" &&
                  order.fulfillment?.status === "handed_out" ? (
                    <Chip
                      size="small"
                      color="success"
                      variant="outlined"
                      icon={
                        <Iconify
                          icon="mdi:hand-extended-outline"
                          width={14}
                        />
                      }
                      label="Ausgegeben"
                      sx={{ alignSelf: "flex-start" }}
                    />
                  ) : null}

                  {order.fulfillment?.type === "shipping" &&
                  order.status !== "canceled" ? (
                    <MemberFulfillmentProgress
                      fulfillmentStatus={order.fulfillment?.status}
                      trackingNumber={order.fulfillment?.trackingNumber}
                    />
                  ) : null}

                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.25,
                      borderRadius: 1.5,
                      bgcolor: "grey.50",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {buildCompactOrderSummary(order.lineItems)}
                      {Array.isArray(order.lineItems) &&
                      order.lineItems.length > 3
                        ? ` und ${
                            order.lineItems.length - 3
                          } weitere Positionen.`
                        : ""}
                    </Typography>
                  </Paper>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontWeight={500}
                    >
                      Gesamt
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      color="primary.main"
                      fontWeight={700}
                    >
                      {toCurrency(order?.totals?.grandTotal)}
                    </Typography>
                  </Stack>

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={{ sm: "center" }}
                  >
                    <Button
                      size="small"
                      onClick={() => onToggleExpanded(order._id)}
                      startIcon={
                        <Iconify
                          icon={
                            isExpanded
                              ? "eva:chevron-up-fill"
                              : "eva:chevron-down-fill"
                          }
                          width={16}
                        />
                      }
                    >
                      {isExpanded
                        ? "Details ausblenden"
                        : "Details anzeigen"}
                    </Button>

                    {isEditable ? (
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          disabled={isBusy || Boolean(activeOrderActionId)}
                          onClick={() => onOpenCancelConfirm(order)}
                          startIcon={<Iconify icon="mdi:cancel" width={16} />}
                        >
                          Stornieren
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={isBusy || Boolean(activeOrderActionId)}
                          onClick={() => onOpenCorrectConfirm(order)}
                          startIcon={
                            <Iconify icon="eva:edit-2-fill" width={15} />
                          }
                        >
                          Korrigieren
                        </Button>
                      </Stack>
                    ) : canDownloadInvoice ? (
                      <Button
                        size="small"
                        variant="contained"
                        color="secondary"
                        disabled={Boolean(activeInvoiceOrderId)}
                        onClick={() => onDownloadInvoice(order)}
                        startIcon={
                          <Iconify icon="eva:download-fill" width={16} />
                        }
                      >
                        {activeInvoiceOrderId === order._id
                          ? "Lade Rechnung..."
                          : "Rechnung herunterladen"}
                      </Button>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Änderungen sind nur im Status "Neu" möglich.
                      </Typography>
                    )}
                  </Stack>

                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Divider sx={{ my: 1 }} />
                    <Stack spacing={1}>
                      {(order.lineItems || []).map((lineItem, index) => {
                        const variantLabel =
                          lineItem.variantLabel &&
                          lineItem.variantLabel !== "Standard"
                            ? lineItem.variantLabel
                            : "Standard";

                        return (
                          <Stack
                            key={`${order._id}-${lineItem.productId}-${lineItem.variantKey}-${index}`}
                            direction="row"
                            justifyContent="space-between"
                            spacing={1}
                            sx={{ py: 0.25 }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600}>
                                {lineItem.productName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {variantLabel} · Menge {lineItem.quantity}
                              </Typography>
                            </Box>
                            <Typography variant="body2" fontWeight={700}>
                              {toCurrency(lineItem.lineTotal)}
                            </Typography>
                          </Stack>
                        );
                      })}
                    </Stack>
                  </Collapse>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      ) : (
        <Paper variant="outlined" sx={{ p: 4.5, textAlign: "center", borderRadius: 2 }}>
          <Iconify
            icon="eva:file-text-outline"
            width={68}
            sx={{ color: "grey.300", mb: 2 }}
          />
          <Typography variant="body1" color="text.secondary" fontWeight={600}>
            Du hast noch keine Bestellungen.
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Bestelle zuerst ein Produkt aus dem Katalog.
          </Typography>
        </Paper>
      )}
    </Stack>
  );
}
